import React, { useEffect, useState, useCallback } from 'react';
import { FaDownload, FaUpload } from 'react-icons/fa';
import Spinner from './spinner';
import { months } from '../util/common';
import ApplicationService from '../services/axios.service';
import { Button } from 'antd';
import { EditOutlined } from "@ant-design/icons";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLoaderStore } from '../util/useLoaderStore';

// Editable flags for each field
const editFlags = {
    total: true,
    databaseLive: false,
    balanceToBill: true,
    risk: true,
    finadjustment: true,
    potential: true,
    deltavsaugbudegt: true,
    deltavsfc5: true,
    gapreason: true,
    reasonforsalesdelta: true,
};

// Add helper functions for dynamic FC labels
const getFcFieldForMonth = (monthValue) => {
    const monthMap = {
        4: 'fc1', 5: 'fc2', 6: 'fc3',
        7: 'fc4', 8: 'fc5', 9: 'fc6',
        10: 'fc7', 11: 'fc8', 12: 'fc9',
        1: 'fc10', 2: 'fc11', 3: 'fc12'
    };
    return monthMap[monthValue] || 'fc1';
};

const getMonthLabel = (monthValue) => {
    const monthLabels = {
        1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr',
        5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug',
        9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec'
    };
    return monthLabels[monthValue] || '';
};

const SalesLapNew = () => {
    const [accordionData, setAccordionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [openIndex, setOpenIndex] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [period, setPeriod] = useState(1);
    const [availableYears, setAvailableYears] = useState([]);
    // State for row-level editing (keys will be "customerIndex-rowId")
    const [editingRows, setEditingRows] = useState({});
    // Store original row data for revert on cancel
    const [originalRows, setOriginalRows] = useState({});
    const [reasons, setReasons] = useState([]);
    const [isValid, setIsValid] = useState(true); // To track the validation state
    const [validationMessage, setValidationMessage] = useState(""); // Message to show error
    const [selectedMonth, setSelectedMonth] = useState(''); // State for selected month
    const navigate = useNavigate();
    const service = new ApplicationService();
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [shouldFetch, setShouldFetch] = useState(false);
    const { startLoading, stopLoading } = useLoaderStore.getState();


    const fetchCustomers = async () => {
        try {
            const response = await service.getSalesTotalCustomers();
            if (response.data.statusCode === 200) {
                const customerList = response.data.data.forecast || [];
                setCustomers(customerList);
                if (customerList.length > 0) {
                    setSelectedCustomer(customerList[0].customer_id);
                }
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
        }
    };

    const fetchLapSummary = useCallback(async () => {
        console.log('🔍 Fetching LAP Summary with:', { selectedCustomer, year, month });
        if (!selectedCustomer || !year || !month ) return;
        
        const monthYear = `${year}-${String(month).padStart(2, '0')}`;
        const payload = {
            month_year: monthYear,
            customer_id: selectedCustomer,
            page: 1,
            limit: 100
        };
        
        console.log('🚀 Calling LAP Summary API with payload:', payload);
        
        setLoading(true);
        try {
            const response = await service.post('sales-total/sales-lap-summary', payload);
            const result = response.data;
            
            console.log('📥 LAP Summary API Response:', result);
            
            if (result.success && result.data) {
                const records = result.data.records || [];
                
                console.log('📊 Records:', records);
                const toNumber = (value) => Number(value || 0);

                const transformedData = records.map((record, index) => ({
                    id: `${record.customer_id}-${record.criteria}-${index}`,
                    customer_id: record.customer_id,
                    customer: record.customer,
                    criteria: record.criteria,
                    [getFcFieldForMonth(month)]: record.forecast_amt || 0,
                    avgBudget: record.budget_amt || 0,
                    total: Number(( toNumber(record.actual_amt) +
                                    toNumber(record.fin_adjustments)+toNumber(record.balance_to_bill)

                                ).toFixed(2)),
                    databaseLive: record.actual_amt || 0,
                    balanceToBill: record.balance_to_bill || 0,
                    risk: Number((
                                    toNumber(record.firm_po_amt) +
                                    toNumber(record.firm_po_risk_amt) +
                                    toNumber(record.fc_vmi_low_risk_amt) +
                                    toNumber(record.fc_vmi_high_risk_amt) +
                                    toNumber(record.potential_high_risk_amt)
                                    ).toFixed(2)),
                    finadjustment: record.fin_adjustments || 0,
                    potential: record.increment_potential || 0,
                    deltavsaugbudegt: (record.forecast_amt || 0) - (record.budget_amt || 0),
                    deltavsfc5: (record.forecast_amt || 0) - (record.actual_amt || 0),
                    gapreason: record.gap_reason || '',
                    reasonforsalesdelta: record.reason_for_sales_delta || ''
                }));
                
                const groupedData = [{
                    customerId: selectedCustomer,
                    customerName: records[0]?.customer || '',
                    tableData: transformedData
                }];
                
                setAccordionData(groupedData);
                setError(null);
            } else {
                setAccordionData([]);
            }
        } catch (err) {
            console.error('❌ LAP Summary Error:', err);
            setError(err.message);
            setAccordionData([]);
        } finally {
            setLoading(false);
            setShouldFetch(false);
        }
    }, [selectedCustomer, year, month, shouldFetch]);

    useEffect(() => {
        const initializeData = async () => {
            await fetchCustomers();
            await fetchYears();
            
            // Fetch reasons
            service.getReasons().then(response => {
                setReasons(response.data);
            }).catch(error => {
                console.error("Error fetching reasons:", error);
            });
            
            // Trigger initial fetch after data is loaded
            setShouldFetch(true);
        };
        
        initializeData();
    }, []);

    useEffect(() => {
        if (shouldFetch && selectedCustomer && year && month) {
            fetchLapSummary();
        }
    }, [shouldFetch, selectedCustomer, year, month, fetchLapSummary]);

    const fetchYears = async () => {
        try {
            const response = await service.getSalesTotalYears();
            const result = response.data;
            if (result.statusCode === 200) {
                const years = result.data;
                setAvailableYears(years);
                setYear(years.length > 0 ? years[0] : new Date().getFullYear());
            } else {
                console.error('Error fetching years:', result.message);
            }
        } catch (error) {
            console.error('Error fetching years:', error);
        }
    };

    // const fetchData = async () => {
    //     const payload = {
    //         year: year,
    //         month: month,
    //         period: period,
    //     }
    //     setLoading(true);
    //     try {
    //         const response = await service.getSalesLab(payload);
    //         const result = response.data;
    //         if (result.statusCode === 200) {
    //             const modifiedData = result.data.map((customer, customerIndex) => ({
    //                 ...customer,
    //                 tableData: customer.tableData.map((row, rowIndex) => ({
    //                     ...row,
    //                     id: row.id || `${customerIndex}-${rowIndex}`,
    //                     customer_id: customer.customerId,
    //                     customer: customer.customerName,
    //                 })),
    //             }));
    //             setAccordionData(modifiedData);
    //             setError(null);
    //         } else {
    //             console.error("Error fetching data:", result.message);
    //             setError(result.message);
    //         }
    //     } catch (err) {
    //         setError(err.message);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    useEffect(() => {
        setSelectedMonth(months.find(m => m.value == month)?.label); // Set the selected month label based on the current month
    }, [month]);

    const fcLabel = getFcFieldForMonth(month);
    const monthLabel = getMonthLabel(month);

    const handlePeriodChange = (e) => {
        const value = e.target.value;
        let newPeriod;

        if (value.startsWith("[") && value.endsWith("]")) {
            // If the value is an array-like string (e.g., "[4,5,6]"), convert it to an array
            newPeriod = JSON.parse(value); // Converts string to actual array [4,5,6]
            setMonth('');
        } else {
            // Otherwise, it's a single number, so we store it as an integer
            newPeriod = parseInt(value);
        }

        setPeriod(newPeriod);

        // Validation logic
        if ([1, 2, 3, 4, 5].includes(newPeriod)) {
            // Period is 1-5 weeks, so year and month must be selected
            if (!year || !month) {
                setIsValid(false);
                setValidationMessage("Please select both Year and Month for 1-5 weeks period.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        } else {
            // For other periods (Quarterly, Half yearly), only year is required
            if (!year) {
                setIsValid(false);
                setValidationMessage("Please select Year for Quarterly or Half Yearly periods.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        }
    };



    const exportData = async () => {
        const monthYear1 = `${year}-${String(month).padStart(2, '0')}`;
        const payload = {
            month_year: monthYear1,
        }
        setExporting(true);
        startLoading();
        try {
            const response = await service.exportSalesLab(payload);
            const result = response.data;
            if (response.status === 200 && result) {
                const fileName = `saleslap_report_${monthYear1}.xlsx`;

                // Create a Blob from the response
                const blob = new Blob([result], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = fileName;

                // Trigger the download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up the URL object
                URL.revokeObjectURL(link.href);
                
                toast.success('Export completed successfully!');
            } else {
                console.error("Error downloading data:", result.message);
                toast.error(result.message || 'Failed to export data');
            }
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.message || 'Failed to export data');
        } finally {
            setExporting(false);
            stopLoading();
        }
    };

    // Helper to form a unique key for each row
    const getRowKey = (customerIndex, rowId) => `${customerIndex}-${rowId}`;

    // When Edit is clicked for a row, store the original data and mark that row as editing
    const handleRowEditClick = (customerIndex, rowId, rowData) => {
        const key = getRowKey(customerIndex, rowId);
        setOriginalRows(prev => ({ ...prev, [key]: rowData }));
        setEditingRows(prev => ({ ...prev, [key]: true }));
    };

    // On Save, you can add your API save logic here. Then remove the row from editing mode.
    const handleRowSaveClick = async (customerIndex, rowId) => {
        const key = getRowKey(customerIndex, rowId);
        // Find the updated row data that you want to save
        const customer = accordionData[customerIndex];
        const rowData = customer.tableData.find((row) => row.id === rowId);
        console.log("Row data to save:", rowData);
        const payload = {
            modelid: rowData.customer_id,
            optionid: rowData.criteria_id,
            date: rowData.date,
            gps_reason: rowData.gapreason,
            balance_to_bill: rowData.balanceToBill,
            risk: rowData.risk,
            fin_adjustment: rowData.finadjustment,
            potential: rowData.potential,
            year: year,
            month: month,
            criteria: rowData.criteria,
            reason_for_sales_delta: rowData.reasonforsalesdelta
        };

        try {
            // Call the API to save the row data.
            // Replace `saveRowData` with your actual API method and adjust parameters as needed.
            const response = await service.updateSalesLap(payload);

            if (response.data.statusCode === 200) {
                // API call was successful; you can add any success notification here if needed.
                console.log("Row updated successfully");
                toast.success("Row updated successfully!");
            } else {
                toast.error("Failed to update row.");
                // Handle any error responses here.
                console.error("Error saving row data:", response.data.message);
            }
        } catch (error) {
            console.error("Error saving row data:", error);
            toast.error("Failed to update row.");
        } finally {
            // Remove the row from edit mode
            setEditingRows((prev) => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
            setOriginalRows((prev) => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
        }
    };


    // When Cancel is clicked, revert the changes using stored original data and exit edit mode.
    const handleRowCancelClick = (customerIndex, rowId) => {
        const key = getRowKey(customerIndex, rowId);
        const originalData = originalRows[key];
        if (originalData) {
            setAccordionData(prevData =>
                prevData.map((customer, cIdx) => {
                    if (cIdx !== customerIndex) return customer;
                    const updatedTableData = customer.tableData.map(row => {
                        if (row.id === rowId) {
                            return { ...originalData };
                        }
                        return row;
                    });
                    return { ...customer, tableData: updatedTableData };
                })
            );
        }
        setEditingRows(prev => {
            const newState = { ...prev };
            delete newState[key];
            return newState;
        });
        setOriginalRows(prev => {
            const newState = { ...prev };
            delete newState[key];
            return newState;
        });
    };

    const handleInputChange = (customerIndex, rowId, field, value) => {
        setAccordionData(prevData =>
            prevData.map((customer, idx) => {
                if (idx !== customerIndex) return customer;
                const updatedTableData = customer.tableData.map(row => {
                    if (row.id === rowId) {
                        return { ...row, [field]: value };
                    }
                    return row;
                });
                return { ...customer, tableData: updatedTableData };
            })
        );
    };

    const handleMonthChange = (e) => {
        const newMonth = e.target.value;
        setMonth(newMonth); // Update the month state
        handleFilterChange(newMonth, year); // Check the validity after the month is updated
    };

    const handleYearChange = (e) => {
        const newYear = e.target.value;
        setYear(newYear); // Update the year state
        handleFilterChange(month, newYear); // Check the validity after the year is updated
    };

    const handleFilterChange = (newMonth, newYear) => {
        // Validate the fields based on the selected period
        if ([1, 2, 3, 4, 5].includes(period)) {
            // Period is 1-5 weeks, so year and month must be selected
            if (!newYear || !newMonth) {
                setIsValid(false);
                setValidationMessage("Please select both Year and Month for 1-5 weeks period.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        } else {
            // For other periods (Quarterly, Half yearly), only year is required
            if (!newYear) {
                setIsValid(false);
                setValidationMessage("Please select Year for Quarterly or Half Yearly periods.");
            } else {
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
            }
        }
    };


    // Cancel editing for all rows in a given customer (accordion) index
    const cancelEditingForCustomer = (customerIndex) => {
        Object.keys(editingRows)
            .filter((key) => key.startsWith(`${customerIndex}-`))
            .forEach((key) => {
                // Extract the rowId by removing the customerIndex and the dash from the key.
                const rowId = key.substring(String(customerIndex).length + 1);
                handleRowCancelClick(customerIndex, rowId);
            });
    };

    // Toggle the accordion for a given index and cancel editing in the previously opened accordion
    const handleAccordionToggle = (index) => {
        if (openIndex !== null && openIndex !== index) {
            cancelEditingForCustomer(openIndex);
        }
        setOpenIndex(openIndex === index ? null : index);
    };


    const handleSearch = () => {
        setSelectedMonth(months.find(m => m.value == month)?.label); // Set the selected month label based on the current month
     
      
            // Period is 1-5 weeks, so year and month must be selected
            if (!year || !month) {
                alert("Please select both Year and Month");
                setIsValid(false);
                setValidationMessage("Please select both Year and Month");
                return; // Prevent search from executing
            } else {
               
                setIsValid(true);
                setValidationMessage(""); // Clear validation message
                 fetchLapSummary();
                // Proceed with the search operation here
                // fetchData(); // Call your fetchData function here
        
        }
    };
    const STICKY_WIDTHS = [110, 140, 80, 110, 110];

    const getStickyStyle = (index, isHeader = false) => {
    if (index >= 5) return {};

    const left = STICKY_WIDTHS
        .slice(0, index)
        .reduce((a, b) => a + b, 0);

    return {
        position: "sticky",
        left,
       zIndex: isHeader ? 50 : 30,
    background: isHeader ? "#F5F7FF" : "#ffffff",
    boxShadow: index === 4 ? "2px 0 6px rgba(0,0,0,0.08)" : "none"
    };
    };

const allRows = accordionData.flatMap(customer => customer.tableData || []);
console.log("All rows for totals calculation:", allRows);
const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const columnTotals = allRows.reduce(
    (totals, row) => {
        totals.forecast += toNumber(row[fcLabel]);
        totals.avgBudget += toNumber(row.avgBudget);
        totals.total += toNumber(row.total);
        totals.databaseLive += toNumber(row.databaseLive);
        totals.balanceToBill += toNumber(row.balanceToBill);
        totals.risk += toNumber(row.risk);
        totals.finadjustment += toNumber(row.finadjustment);
        totals.potential += toNumber(row.potential);
        totals.deltaVsBudget += toNumber(row.deltavsaugbudegt);
        totals.deltaVsForecast += toNumber(row.deltavsfc5);

        return totals;
    },
    {
        forecast: 0,
        avgBudget: 0,
        total: 0,
        databaseLive: 0,
        balanceToBill: 0,
        risk: 0,
        finadjustment: 0,
        potential: 0,
        deltaVsBudget: 0,
        deltaVsForecast: 0
    }
);
 
    useEffect(() => {
        fetchYears();
        // fetchData();
    }, []);

    return (
        <div className="p-4 bg-white w-full mb-4 shadow-md rounded-md">
            {/* Filter Row */}
            <div className="p-4">
                 <div className="flex">
                <h3 className="text-lg font-semibold text-[#1D1D1D] mb-4">Customers</h3>
                </div>
                <div className="flex gap-x-5 mb-4">
                    <div className="md:w-1/5 w-full">
                        <label htmlFor="customer" className="block text-sm font-semibold text-gray-700 mb-1">
                            Customer
                        </label>
                        <select
                            id="customer"
                            value={selectedCustomer || ''}
                            onChange={(e) => setSelectedCustomer(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1 w-full h-10 cursor-pointer"
                        >
                            {customers.map(customer => (
                                <option key={customer.customer_id} value={customer.customer_id}>
                                    {customer.customer_name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:w-1/5 w-full">
                        <label htmlFor="year" className="block text-sm font-semibold text-gray-700 mb-1">
                            Year
                        </label>
                        <select id="year" className="border border-gray-300 rounded px-2 py-1 w-full h-10 cursor-pointer" value={year} onChange={handleYearChange}>
                            <option value="">Select Year</option>
                            {availableYears.map((yr, idx) => (
                                <option key={idx} value={yr}>
                                    {yr}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="md:w-1/5 w-full">
                        <label htmlFor="month" className="block text-sm font-semibold text-gray-700 mb-1">
                            Month
                        </label>
                        <select
                            id="month"
                            value={month}
                            onChange={handleMonthChange}
                            className="w-full border border-gray-300 rounded px-2 py-1 h-10 cursor-pointer"
                        >
                            <option value="">Select Month</option>
                            {months.map((m, index) => (
                                <option key={index} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* <div className="md:w-1/5 w-full">
                        <label htmlFor="period" className="block text-sm font-semibold text-gray-700 mb-1">
                            Period
                        </label>
                        <select
                            id="period"
                            className="border border-gray-300 rounded px-2 py-1 w-full h-10 cursor-pointer"
                          
                            value={Array.isArray(period) ? JSON.stringify(period) : period} // Convert array to string for comparison
                            onChange={handlePeriodChange}
                        >
                            <option value="">Select Period</option>
                            <option value="1">1 - Week</option>
                            <option value="2">2 - Weeks</option>
                            <option value="3">3 - Weeks</option>
                            <option value="4">4 - Weeks</option>
                            <option value="5">5 - Weeks</option>
                            <option value="[4,5,6]">Quarterly 1</option>
                            <option value="[7,8,9]">Quarterly 2</option>
                            <option value="[10,11,12]">Quarterly 3</option>
                            <option value="[1,2,3]">Quarterly 4</option>
                            <option value="[4,5,6,7,8,9]">Half yearly 1</option>
                            <option value="[10,11,12,1,2,3]">Half yearly 2</option>
                        </select>
                    </div> */}
                    <div className="md:w-1/5 w-full">
                        <label className="block text-sm text-gray-600 mb-1 invisible">Search</label>
                        <button
                            className={`bg-[#3D3D3D] w-full h-10 rounded-md ${isValid ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            onClick={handleSearch} disabled={!isValid}>
                            <span className="text-white">Search</span>
                        </button>
                    </div>
                      <div className="md:w-1/5 w-full flex gap-x-5 py-2 items-center mt-4">
                        <button className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={() => navigate("/upload-saleslap-excel")}>
                            <FaUpload className="text-white" />
                            <span className="text-white">Import</span>
                        </button>
                        <button className="bg-[#107C41] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={exportData}>
                            <FaDownload className="text-white" />
                            <span className="text-white">Export</span>
                        </button>
                    </div>
                </div>
                {!isValid && (
                    <div className="text-red-500 mt-2">{validationMessage}</div>
                )}
            </div>
            {/* Main Content */}
            {/* <div className="flex">
                <h3 className="text-lg font-semibold text-[#1D1D1D] mb-4">Customers</h3> */}
                {/* <div className="ml-auto flex gap-x-5 py-2">
                    <button className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={() => navigate("/upload-saleslap-excel")}>
                        <FaUpload className="text-white" />
                        <span className="text-white">Import</span>
                    </button>
                    <button className="bg-[#107C41] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer" onClick={exportData}>
                        <FaDownload className="text-white" />
                        <span className="text-white">Export</span>
                    </button>
                </div> */}
            {/* </div> */}
            {loading ? (
                <div>
                    <Spinner />
                </div>
            ) : error ? (
                <div>Error: {error}</div>
            ) : (
                <div className="overflow-x-auto relative">
                    <table className="min-w-max w-full text-sm border-y border-[#E6E6E6]">
                        <thead className="bg-[#F5F7FF] text-[#000000] border-y border-[#E6E6E6] sticky top-0 z-20">
                            <tr>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Customer</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Criteria</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">{monthLabel} {fcLabel.toUpperCase()}</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">{monthLabel} Budget</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Total</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Database LIVE</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Balance to bill</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Risk/Potential</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Fin adjustment</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Incremental Potential</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Delta vs {monthLabel} Budget</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Delta vs {monthLabel} {fcLabel.toUpperCase()}</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Gap Reason</th>
                                <th className="border-r border-[#E5E7EB] py-2 px-3 font-semibold text-center">Reason for sales</th>
                                <th className="py-2 px-3 font-semibold text-center">Database</th>
                            </tr>
                        </thead>
                        <tbody>
                        {(allRows.length > 0) &&
                        <tr className="border-t-2 border-[#E6E6E6] border-b-2">
                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                        
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            Total
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.forecast.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.avgBudget.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.total.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.databaseLive.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.balanceToBill.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.risk.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.finadjustment.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.potential.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.deltaVsBudget.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            {columnTotals.deltaVsForecast.toLocaleString()}
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            -
                        </td>

                        <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                            -
                        </td>

                        <td className="py-2 px-3 text-center">
                            -
                        </td>
                    </tr>
}
                            {accordionData.map((customer, customerIndex) => (
                                customer.tableData.map((row, rowIndex) => {
                                    const key = getRowKey(customerIndex, row.id);
                                    const isEditing = editingRows[key];
                                    
                                    return (
                                        <tr key={row.id} className="border-b border-[#E6E6E6]">
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.customer}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.criteria}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row[fcLabel] || 0}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.avgBudget || 0}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.total || 0}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.databaseLive || 0}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        value={row.balanceToBill || 0}
                                                        onChange={(e) => handleInputChange(customerIndex, row.id, 'balanceToBill', e.target.value)}
                                                        className="w-full px-2 py-1 border rounded"
                                                    />
                                                ) : (
                                                    row.balanceToBill || 0
                                                )}
                                            </td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        value={row.risk || 0}
                                                        onChange={(e) => handleInputChange(customerIndex, row.id, 'risk', e.target.value)}
                                                        className="w-full px-2 py-1 border rounded"
                                                    />
                                                ) : (
                                                    row.risk || 0
                                                )}
                                            </td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        value={row.finadjustment || 0}
                                                        onChange={(e) => handleInputChange(customerIndex, row.id, 'finadjustment', e.target.value)}
                                                        className="w-full px-2 py-1 border rounded"
                                                    />
                                                ) : (
                                                    row.finadjustment || 0
                                                )}
                                            </td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        value={row.potential || 0}
                                                        onChange={(e) => handleInputChange(customerIndex, row.id, 'potential', e.target.value)}
                                                        className="w-full px-2 py-1 border rounded"
                                                    />
                                                ) : (
                                                    row.potential || 0
                                                )}
                                            </td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.deltavsaugbudegt || 0}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">{row.deltavsfc5 || 0}</td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3 text-center">
                                                {isEditing ? (
                                                    <select
                                                        value={row.gapreason || ''}
                                                        onChange={(e) => handleInputChange(customerIndex, row.id, 'gapreason', e.target.value)}
                                                        className="w-full px-2 py-1 border rounded"
                                                    >
                                                        <option value="">Select Reason</option>
                                                        {reasons.map((reason) => (
                                                            <option key={reason.id} value={reason.name}>
                                                                {reason.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    reasons.find(r => r.id == row.gapreason)?.name || row.gapreason || ''
                                                )}
                                            </td>
                                            <td className="border-r border-[#E5E7EB] py-2 px-3">
                                                {isEditing ? (
                                                    <textarea
                                                        value={row.reasonforsalesdelta || ''}
                                                        onChange={(e) => handleInputChange(customerIndex, row.id, 'reasonforsalesdelta', e.target.value)}
                                                        className="w-full px-2 py-1 border rounded"
                                                        rows="2"
                                                    />
                                                ) : (
                                                    row.reasonforsalesdelta || ''
                                                )}
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                                {isEditing ? (
                                                    <div className="flex gap-2">
                                                        <button onClick={() => handleRowSaveClick(customerIndex, row.id)} className="text-green-600">Save</button>
                                                        <button onClick={() => handleRowCancelClick(customerIndex, row.id)} className="text-red-600">Cancel</button>
                                                    </div>
                                                ) : (
                                                    <button onClick={() => handleRowEditClick(customerIndex, row.id, row)} className="text-blue-600">
                                                        <EditOutlined />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SalesLapNew;
